import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { useOAuthConsentPopup } from '@/hooks/use-oauth-consent-popup';
import {
  listPersonalConnections,
  startPersonalConnection,
} from '@/services/personal-connections/personal-connections';
import { PersonalConnection } from '@/services/personal-connections/types';

import { isConsentSettled } from './helpers/connection-state';
import { personalConnectionsQueryKey } from './use-personal-connections';

/**
 * Connect any provider in a consent popup (Composio's hosted consent for
 * Gmail / Drive / Attio, the provider's own OAuth screen for Sentra) and
 * poll the one status route until it reports connected.
 */
export const useConnectorPopup = (orgId?: string) => {
  const notification = useNotification();
  const queryClient = useQueryClient();
  const consent = useOAuthConsentPopup('connector-connect');

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: personalConnectionsQueryKey(orgId),
      }),
    [queryClient, orgId],
  );

  const connect = useCallback(
    async (connection: PersonalConnection) => {
      const provider = connection.provider;
      try {
        await consent.start(
          provider,
          async () => {
            const result = await startPersonalConnection(orgId!, provider);
            return {
              redirectUrl: result.redirect_url,
              isSettled: async () => {
                const fresh = await listPersonalConnections(orgId!);
                const row = fresh.connections.find(
                  (c) => c.provider === provider,
                );
                if (!isConsentSettled(row)) return false;
                queryClient.setQueryData(
                  personalConnectionsQueryKey(orgId),
                  fresh,
                );
                return true;
              },
            };
          },
          {
            onConnected: () =>
              notification.success?.(`${connection.name} connected`),
            onAbandoned: () => void refresh(),
            onTimedOut: () => {
              void refresh();
              notification.error(
                `${connection.name} sign-in timed out. Start it again from Connectors.`,
              );
            },
          },
        );
      } catch (error) {
        Sentry.captureException(error, { extra: { orgId, provider } });
        notification.error(
          error instanceof Error
            ? error.message
            : 'Could not start the sign-in',
        );
      }
    },
    [consent, notification, orgId, queryClient, refresh],
  );

  return { connect, isConnecting: consent.isWaiting };
};
